import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import supabase from '../../supabase/supabase';

import {
  getQuickBooksSourceAccounts,
  getQuickBooksSourceTransactions,
  getProfitYoAccountsForQuickBooks,
  saveQuickBooksMappings,
  previewQuickBooksTransactions,
  stageQuickBooksTransactions
} from '../../services/quickbooksService';

import SearchableSelect from '../common/SearchableSelect';

import {
  FiX,
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiLink2,
  FiRefreshCw,
  FiSearch
} from 'react-icons/fi';

/* =========================================================
   HELPERS
========================================================= */

const str = (value) => String(value ?? '').trim();

const localDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const initialDates = () => {
  const now = new Date();

  return {
    startDate: localDate(
      new Date(now.getFullYear(), now.getMonth(), 1)
    ),
    endDate: localDate(now)
  };
};

const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const parsed = new Date(`${value}T00:00:00Z`);

  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
};

const formatMoney = (value, currency = 'USD') => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) return '—';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const postingKey = (row) => str(row?.postingKey);

const groupKey = (row) => {
  const id = str(row?.quickbooksAccountId);

  if (id) return `id:${id}`;

  return `name:${str(row?.accountName).toLowerCase()}`;
};

const rowStatus = (row) => {
  if (row?.isDuplicate || row?.status === 'duplicate') {
    return 'duplicate';
  }

  if (
    row?.accountMatched &&
    !row?.needsReview &&
    row?.status === 'ready'
  ) {
    return 'ready';
  }

  return 'needs_mapping';
};

const MAX_SELECTED = 1000;

const steps = [
  'Date Range',
  'Preview',
  'Match Accounts',
  'Review & Stage',
  'Finish'
];

/* =========================================================
   UI COMPONENTS
========================================================= */

function SummaryCard({ label, value, color = 'slate' }) {
  const colors = {
    slate: 'bg-slate-50 text-slate-900',
    green: 'bg-emerald-50 text-emerald-800',
    amber: 'bg-amber-50 text-amber-800',
    blue: 'bg-blue-50 text-blue-800'
  };

  return (
    <div className={`rounded-2xl p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-semibold">
        {value}
      </p>
    </div>
  );
}

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div
            className={`flex shrink-0 items-center gap-2 text-xs font-semibold ${
              index <= step
                ? 'text-slate-900'
                : 'text-slate-400'
            }`}
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                index < step
                  ? 'bg-emerald-600 text-white'
                  : index === step
                    ? 'bg-slate-950 text-white'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {index < step ? <FiCheckCircle /> : index + 1}
            </span>

            <span className="hidden md:inline">{label}</span>
          </div>

          {index < steps.length - 1 && (
            <div className="h-px min-w-3 flex-1 bg-slate-200" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* =========================================================
   LEGACY MODAL IMPLEMENTATION

   Retained so unrelated QuickBooks code and UI copy are not
   discarded. The default export below is the source-account
   workflow; this component is not mounted by the application.
========================================================= */

function LegacyQuickBooksImportModal({
  isOpen,
  onClose,
  organizationId,
  organizationName = '',
  organizationCurrency = 'USD',
  externalError = null,
  error: legacyError = null,
  onPreview = previewQuickBooksTransactions,
  onStage = stageQuickBooksTransactions
}) {
  const [step, setStep] = useState(0);
  const [dates, setDates] = useState(initialDates);

  const [rows, setRows] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [realmId, setRealmId] = useState('');
  const [connectionId, setConnectionId] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [assignments, setAssignments] = useState({});
  const [savingMappings, setSavingMappings] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [accountFilter, setAccountFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showDuplicates, setShowDuplicates] = useState(false);

  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [result, setResult] = useState(null);

  const requestRef = useRef(0);
  const scopeRef = useRef(organizationId);
  const mountedRef = useRef(false);

  /* =======================================================
     LIFECYCLE
  ======================================================= */

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    scopeRef.current = organizationId;
    requestRef.current += 1;

    setStep(0);
    setDates(initialDates());
    setRows([]);
    setCompanyName('');
    setRealmId('');
    setConnectionId('');

    setAccounts([]);
    setAssignments({});
    setSelectedKeys([]);
    setAccountFilter('all');
    setSearch('');
    setShowDuplicates(false);

    setBusy(false);
    setSavingMappings(false);
    setErrorMessage('');
    setSuccessMessage('');
    setResult(null);
  }, [isOpen, organizationId]);

  /* =======================================================
     LOAD EXISTING PROFITYO ACCOUNTS
  ======================================================= */

  useEffect(() => {
    if (!isOpen || !organizationId) return;

    let active = true;

    const loadAccounts = async () => {
      setAccountsLoading(true);

      try {
        const all = [];
        let offset = 0;
        const pageSize = 1000;

        while (true) {
          const { data, error } = await supabase
            .schema('accounting')
            .from('accounts')
            .select('id, code, name, type, sub_type, is_active')
            .eq('organization_id', organizationId)
            .eq('is_active', true)
            .order('code', { ascending: true })
            .range(offset, offset + pageSize - 1);

          if (error) throw error;

          all.push(...(data || []));

          if (!data || data.length < pageSize) break;

          offset += pageSize;

          if (offset >= 10000) {
            throw new Error(
              'Too many ProfitYo accounts to load safely.'
            );
          }
        }

        if (active) setAccounts(all);
      } catch (error) {
        console.error('QuickBooks account loading:', error);

        if (active) {
          setErrorMessage(
            'Unable to load the ProfitYo Chart of Accounts.'
          );
        }
      } finally {
        if (active) setAccountsLoading(false);
      }
    };

    loadAccounts();

    return () => {
      active = false;
    };
  }, [isOpen, organizationId]);

  /* =======================================================
     VALIDATE DATE RANGE
  ======================================================= */

  const rangeValid = useMemo(() => {
    if (!validDate(dates.startDate) || !validDate(dates.endDate)) {
      return false;
    }

    const days =
      (Date.parse(dates.endDate) - Date.parse(dates.startDate)) /
      86400000;

    return days >= 0 && days <= 366;
  }, [dates]);

  /* =======================================================
     ACCOUNT GROUPING

     A QuickBooks account can have many postings.
     Each account only needs one saved mapping.
  ======================================================= */

  const accountGroups = useMemo(() => {
    const groups = new Map();

    for (const row of rows) {
      const key = groupKey(row);

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          quickbooksAccountId: str(row.quickbooksAccountId),
          accountName: str(row.accountName) || 'Unknown account',
          profitYoAccountName: str(row.profitYoAccountName),
          profitYoAccountId: row.accountId || null,
          total: 0,
          duplicates: 0,
          unmatched: 0
        });
      }

      const group = groups.get(key);

      group.total += 1;

      if (rowStatus(row) === 'duplicate') {
        group.duplicates += 1;
      }

      if (
        rowStatus(row) !== 'duplicate' &&
        !row.accountMatched
      ) {
        group.unmatched += 1;
      }
    }

    return [...groups.values()].sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }, [rows]);

  const unmatchedGroups = useMemo(
    () => accountGroups.filter((group) => group.unmatched > 0),
    [accountGroups]
  );

  const counts = useMemo(() => ({
    total: rows.length,
    ready: rows.filter((row) => rowStatus(row) === 'ready').length,
    duplicates: rows.filter(
      (row) => rowStatus(row) === 'duplicate'
    ).length,
    unmatched: rows.filter(
      (row) => rowStatus(row) === 'needs_mapping'
    ).length
  }), [rows]);

  /* =======================================================
     POSTING SELECTION
  ======================================================= */

  const available = useMemo(
    () => rows.filter(
      (row) =>
        rowStatus(row) === 'ready' &&
        /^[a-f0-9]{64}$/i.test(postingKey(row))
    ),
    [rows]
  );

  const availableKeySet = useMemo(
    () => new Set(available.map(postingKey)),
    [available]
  );

  const selectedSet = useMemo(
    () => new Set(
      selectedKeys.filter((key) => availableKeySet.has(key))
    ),
    [selectedKeys, availableKeySet]
  );

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        !showDuplicates &&
        rowStatus(row) === 'duplicate'
      ) {
        return false;
      }

      if (
        accountFilter !== 'all' &&
        groupKey(row) !== accountFilter
      ) {
        return false;
      }

      if (!needle) return true;

      return [
        row.date,
        row.description,
        row.accountName,
        row.profitYoAccountName,
        row.transactionId,
        row.transactionType,
        row.quickbooksAccountId
      ]
        .map(str)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, search, accountFilter, showDuplicates]);

  const filteredEligible = useMemo(
    () => filteredRows.filter(
      (row) => availableKeySet.has(postingKey(row))
    ),
    [filteredRows, availableKeySet]
  );

  const allShownSelected =
    filteredEligible.length > 0 &&
    filteredEligible.every(
      (row) => selectedSet.has(postingKey(row))
    );

  /* =======================================================
     CLOSE
  ======================================================= */

  const close = () => {
    if (busy || savingMappings) return;

    requestRef.current += 1;
    onClose?.();
  };

  /* =======================================================
     PREVIEW QUICKBOOKS TRANSACTIONS
  ======================================================= */

  const preview = async () => {
    if (!organizationId) {
      setErrorMessage('Select a client organization first.');
      return;
    }

    if (!rangeValid) {
      setErrorMessage(
        'Choose a valid date range of 366 days or fewer.'
      );
      return;
    }

    const token = ++requestRef.current;
    const scope = organizationId;

    setBusy(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const data = await onPreview({
        organizationId,
        startDate: dates.startDate,
        endDate: dates.endDate
      });

      if (
        !mountedRef.current ||
        token !== requestRef.current ||
        scope !== scopeRef.current
      ) {
        return;
      }

      if (
        data?.success !== true ||
        !Array.isArray(data.transactions)
      ) {
        throw new Error(
          data?.error ||
          'QuickBooks did not return a valid transaction preview.'
        );
      }

      const previewRows = data.transactions;
      const keys = previewRows.map(postingKey);

      if (
        keys.some((key) => !/^[a-f0-9]{64}$/i.test(key)) ||
        new Set(keys).size !== keys.length
      ) {
        throw new Error(
          'QuickBooks returned invalid or repeated posting keys.'
        );
      }

      setRows(previewRows);
      setCompanyName(data.companyName || '');
      setConnectionId(data.connectionId || '');
      setRealmId(data.realmId || '');

      setSelectedKeys(
        previewRows
          .filter((row) => rowStatus(row) === 'ready')
          .map(postingKey)
          .slice(0, MAX_SELECTED)
      );

      setAssignments({});
      setAccountFilter('all');
      setSearch('');
      setShowDuplicates(false);

      setStep(1);
    } catch (error) {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setErrorMessage(
          error?.message ||
          'Unable to preview QuickBooks transactions.'
        );
      }
    } finally {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setBusy(false);
      }
    }
  };

  /* =======================================================
     ACCOUNT MAPPING

     This is NOT categorization.

     Each QuickBooks account is linked to an existing
     ProfitYo Chart of Accounts entry.

     The backend must validate and persist mappings.
  ======================================================= */

  const updateAssignment = (group, accountId) => {
    setAssignments((current) => ({
      ...current,
      [group.key]: accountId || ''
    }));

    setErrorMessage('');
    setSuccessMessage('');
  };

  const selectedMappings = useMemo(() => {
    return unmatchedGroups
      .filter((group) => (
        Boolean(group.quickbooksAccountId) &&
        Boolean(assignments[group.key])
      ))
      .map((group) => ({
        quickbooksAccountId: group.quickbooksAccountId,
        quickbooksAccountName: group.accountName,
        profitYoAccountId: assignments[group.key]
      }));
  }, [unmatchedGroups, assignments]);

  const saveMappings = async () => {
    if (selectedMappings.length === 0) {
      setErrorMessage(
        'Select a ProfitYo account for at least one unmatched QuickBooks account.'
      );
      return;
    }

    const token = ++requestRef.current;
    const scope = organizationId;

    setSavingMappings(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Requires the save-mappings action in
      // quickbooks-import/index.ts.
      //
      // Do not write to the service-only mapping table
      // directly from the browser.

      const { data, error } = await supabase.functions.invoke(
        'quickbooks-import',
        {
          body: {
            action: 'save-mappings',
            organizationId,
            connectionId,
            mappings: selectedMappings
          }
        }
      );

      if (error) {
        let detail = error.message;

        try {
          const body = await error.context?.json();
          detail = body?.error || body?.message || detail;
        } catch {
          // Keep the original error.
        }

        throw new Error(detail);
      }

      if (data?.success !== true) {
        throw new Error(
          data?.error ||
          data?.message ||
          'Account mappings could not be saved.'
        );
      }

      if (
        !mountedRef.current ||
        token !== requestRef.current ||
        scope !== scopeRef.current
      ) {
        return;
      }

      setSuccessMessage(
        'Account mappings saved. Refreshing QuickBooks preview...'
      );

      // Re-preview from the server so that the updated
      // account mappings are verified by the backend.
      const refreshed = await onPreview({
        organizationId,
        startDate: dates.startDate,
        endDate: dates.endDate
      });

      if (
        !mountedRef.current ||
        token !== requestRef.current ||
        scope !== scopeRef.current
      ) {
        return;
      }

      if (
        refreshed?.success !== true ||
        !Array.isArray(refreshed.transactions)
      ) {
        throw new Error(
          'Mappings were saved, but the preview could not be refreshed.'
        );
      }

      setRows(refreshed.transactions);
      setAssignments({});

      setSelectedKeys(
        refreshed.transactions
          .filter((row) => rowStatus(row) === 'ready')
          .map(postingKey)
          .slice(0, MAX_SELECTED)
      );

      setSuccessMessage(
        'Account mappings saved and verified successfully.'
      );
    } catch (error) {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setErrorMessage(
          error?.message ||
          'Unable to save account mappings.'
        );
      }
    } finally {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setSavingMappings(false);
      }
    }
  };

  /* =======================================================
     SELECTION
  ======================================================= */

  const toggleOne = (key) => {
    setSelectedKeys((current) => {
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }

      if (current.length >= MAX_SELECTED) {
        return current;
      }

      return [...current, key];
    });
  };

  const toggleShown = () => {
    const visible = filteredEligible.map(postingKey);
    const visibleSet = new Set(visible);

    setSelectedKeys((current) => {
      if (allShownSelected) {
        return current.filter((key) => !visibleSet.has(key));
      }

      return [...new Set([...current, ...visible])]
        .slice(0, MAX_SELECTED);
    });
  };

  /* =======================================================
     STAGE TRANSACTIONS

     Stage only.
     Do not post to accounting.transactions.
  ======================================================= */

  const stage = async () => {
    const keys = [...selectedSet];

    if (keys.length === 0) {
      setErrorMessage(
        'Select at least one mapped posting to stage.'
      );
      return;
    }

    if (keys.length > MAX_SELECTED) {
      setErrorMessage(
        `Only ${MAX_SELECTED} postings can be staged at once.`
      );
      return;
    }

    const token = ++requestRef.current;
    const scope = organizationId;

    setBusy(true);
    setErrorMessage('');

    try {
      const data = await onStage({
        organizationId,
        startDate: dates.startDate,
        endDate: dates.endDate,
        selectedKeys: keys,
        connectionId
      });

      if (
        !mountedRef.current ||
        token !== requestRef.current ||
        scope !== scopeRef.current
      ) {
        return;
      }

      if (
        data?.success !== true ||
        data?.action !== 'stage'
      ) {
        throw new Error(
          data?.error ||
          'QuickBooks staging was not confirmed.'
        );
      }

      setResult(data);
      setStep(4);
    } catch (error) {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setErrorMessage(
          error?.message ||
          'Unable to stage QuickBooks transactions.'
        );
      }
    } finally {
      if (
        mountedRef.current &&
        token === requestRef.current &&
        scope === scopeRef.current
      ) {
        setBusy(false);
      }
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-sm">

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="qb-import-title"
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      >

        {/* HEADER */}

        <header className="flex items-start justify-between border-b border-slate-100 px-5 py-5 md:px-7">

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
              ProfitYo · QuickBooks Online
            </p>

            <h2
              id="qb-import-title"
              className="mt-1 text-xl font-semibold text-slate-950"
            >
              Import transactions
            </h2>

            <p className="mt-1 text-xs text-slate-500">
              {organizationName || 'Active organization'}
              {companyName ? ` · ${companyName}` : ''}
            </p>
          </div>

          <button
            type="button"
            onClick={close}
            disabled={busy || savingMappings}
            className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            aria-label="Close QuickBooks import"
          >
            <FiX size={20} />
          </button>
        </header>

        {/* STEPS */}

        <div className="border-b border-slate-100 px-5 py-4 md:px-7">
          <StepBar step={step} />
        </div>

        {/* CONTENT */}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-7">

          {/* ERRORS */}

          {(errorMessage || externalError || legacyError) && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" />

              <p>
                {errorMessage || externalError || legacyError}
              </p>
            </div>
          )}

          {successMessage && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
              <FiCheckCircle />
              <p>{successMessage}</p>
            </div>
          )}

          {/* ===============================================
             STEP 0 — DATE RANGE
          =============================================== */}

          {step === 0 && (
            <div className="mx-auto max-w-2xl space-y-6 py-8">

              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Select transaction period
                </h3>

                <p className="mt-2 text-sm text-slate-500">
                  Retrieve existing QuickBooks accounting
                  activity across all accounts.
                  No AI categorization is needed.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-semibold">
                    Start date
                  </span>

                  <input
                    type="date"
                    value={dates.startDate}
                    onChange={(event) =>
                      setDates((current) => ({
                        ...current,
                        startDate: event.target.value
                      }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-4 py-3"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-semibold">
                    End date
                  </span>

                  <input
                    type="date"
                    value={dates.endDate}
                    onChange={(event) =>
                      setDates((current) => ({
                        ...current,
                        endDate: event.target.value
                      }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-4 py-3"
                  />
                </label>
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                QuickBooks classifications are preserved.
                ProfitYo only matches existing QuickBooks
                accounts to your Chart of Accounts.
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">
                This workflow currently previews and stages
                General Ledger postings. Final debit/credit
                validation and ledger approval are not enabled.
              </div>

            </div>
          )}

          {/* ===============================================
             STEP 1 — PREVIEW
          =============================================== */}

          {step === 1 && (
            <div className="space-y-5">

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <SummaryCard label="Report postings" value={counts.total} />
                <SummaryCard label="Mapped" value={counts.ready} color="green" />
                <SummaryCard label="Need mapping" value={counts.unmatched} color="amber" />
                <SummaryCard label="Previously staged" value={counts.duplicates} />
              </div>

              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  QuickBooks account summary
                </h3>

                <p className="mt-1 text-xs text-slate-500">
                  Each QuickBooks account may contain many
                  postings. You only need to map each account once.
                </p>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="max-h-[350px] overflow-y-auto">

                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3">QuickBooks account</th>
                        <th className="px-4 py-3">ProfitYo account</th>
                        <th className="px-4 py-3 text-right">Postings</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {accountGroups.map((group) => (
                        <tr key={group.key}>
                          <td className="px-4 py-3 font-medium">
                            {group.accountName}
                          </td>

                          <td className="px-4 py-3 text-slate-600">
                            {group.profitYoAccountName || 'Not mapped'}
                          </td>

                          <td className="px-4 py-3 text-right">
                            {group.total}
                          </td>

                          <td className="px-4 py-3">
                            {group.unmatched > 0 ? (
                              <span className="text-xs font-semibold text-amber-700">
                                Needs mapping
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-emerald-700">
                                Mapped / reviewed
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}

                      {accountGroups.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-slate-500">
                            No account activity found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                </div>
              </div>

              {counts.unmatched > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  <strong>{counts.unmatched} postings need account mapping.</strong>
                  {' '}You can resolve them in the next step.
                </div>
              )}

            </div>
          )}

          {/* ===============================================
             STEP 2 — MATCH ACCOUNTS
          =============================================== */}

          {step === 2 && (
            <div className="space-y-5">

              <div className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                <FiLink2 className="mt-1 shrink-0 text-blue-700" />

                <div>
                  <h3 className="text-sm font-semibold text-blue-900">
                    Match existing accounts
                  </h3>

                  <p className="mt-1 text-xs leading-5 text-blue-700">
                    QuickBooks already categorized these postings.
                    Select the equivalent existing ProfitYo
                    account for each unmatched QuickBooks account.
                    No new accounts will be created.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryCard
                  label="QuickBooks accounts"
                  value={accountGroups.length}
                />

                <SummaryCard
                  label="Unmatched accounts"
                  value={unmatchedGroups.length}
                  color="amber"
                />

                <SummaryCard
                  label="Mappings selected"
                  value={selectedMappings.length}
                  color="green"
                />
              </div>

              {unmatchedGroups.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                  <FiCheckCircle className="mx-auto text-3xl text-emerald-600" />

                  <h3 className="mt-3 font-semibold text-emerald-900">
                    No unmatched accounts
                  </h3>

                  <p className="mt-2 text-sm text-emerald-700">
                    All eligible QuickBooks accounts have
                    existing ProfitYo mappings.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {unmatchedGroups.map((group) => (
                    <div
                      key={group.key}
                      className="rounded-2xl border border-slate-200 p-4"
                    >
                      <div className="grid gap-4 lg:grid-cols-2">

                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            QuickBooks account
                          </p>

                          <p className="mt-1 text-sm font-semibold text-slate-900">
                            {group.accountName}
                          </p>

                          <p className="mt-1 text-xs text-slate-500">
                            {group.unmatched} unmatched posting(s)
                          </p>

                          {!group.quickbooksAccountId && (
                            <p className="mt-2 text-xs text-rose-600">
                              QuickBooks account ID could not be
                              verified. This account cannot be
                              mapped automatically.
                            </p>
                          )}
                        </div>

                        <div>
                          <p className="mb-2 text-xs font-semibold text-slate-700">
                            Corresponding ProfitYo account
                          </p>

                          <SearchableSelect
                            options={accounts}
                            value={assignments[group.key] || ''}
                            onChange={(value) =>
                              updateAssignment(group, value)
                            }
                            disabled={
                              accountsLoading ||
                              savingMappings ||
                              !group.quickbooksAccountId
                            }
                            placeholder={
                              accountsLoading
                                ? 'Loading accounts...'
                                : 'Select existing account...'
                            }
                            searchPlaceholder="Search Chart of Accounts..."
                            emptyMessage="No active accounts available."
                            getLabel={(account) =>
                              `${account.code || 'No code'} - ${account.name}`
                            }
                            getSearchText={(account) => [
                              account.code,
                              account.name,
                              account.type,
                              account.sub_type
                            ]}
                            menuWidth={420}
                          />
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              )}

              {unmatchedGroups.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs text-slate-600">
                    Mappings must be saved and verified
                    before their postings become eligible.
                  </p>

                  <button
                    type="button"
                    onClick={saveMappings}
                    disabled={
                      savingMappings ||
                      busy ||
                      selectedMappings.length === 0
                    }
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {savingMappings ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiLink2 />
                    )}

                    Save {selectedMappings.length} mapping(s)
                  </button>
                </div>
              )}

            </div>
          )}

          {/* ===============================================
             STEP 3 — REVIEW & STAGE
          =============================================== */}

          {step === 3 && (
            <div className="space-y-5">

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <SummaryCard label="Total postings" value={counts.total} />
                <SummaryCard label="Eligible" value={counts.ready} color="green" />
                <SummaryCard label="Unmatched" value={counts.unmatched} color="amber" />
                <SummaryCard label="Duplicates" value={counts.duplicates} />
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs leading-5 text-blue-800">
                QuickBooks classifications are preserved.
                Staging records source postings for accounting
                review only. It does not post to the General Ledger.
              </div>

              <div className="flex flex-wrap gap-3">

                <div className="relative min-w-[220px] flex-1">
                  <FiSearch className="absolute left-3 top-3.5 text-slate-400" />

                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search transactions..."
                    className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-sm"
                  />
                </div>

                <select
                  value={accountFilter}
                  onChange={(event) => setAccountFilter(event.target.value)}
                  className="max-w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="all">All QuickBooks accounts</option>

                  {accountGroups.map((group) => (
                    <option key={group.key} value={group.key}>
                      {group.accountName} ({group.total})
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={toggleShown}
                  disabled={filteredEligible.length === 0}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {allShownSelected
                    ? 'Deselect shown'
                    : 'Select eligible shown'}
                </button>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={showDuplicates}
                  onChange={(event) =>
                    setShowDuplicates(event.target.checked)
                  }
                />

                Show previously staged postings
              </label>

              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="max-h-[440px] overflow-auto">

                  <table className="w-full min-w-[1000px] text-left text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3">Select</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Transaction / Memo</th>
                        <th className="px-4 py-3">QuickBooks account</th>
                        <th className="px-4 py-3">ProfitYo account</th>
                        <th className="px-4 py-3 text-right">Report amount</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {filteredRows.map((row, index) => {
                        const key = postingKey(row);
                        const status = rowStatus(row);
                        const eligible = availableKeySet.has(key);

                        return (
                          <tr
                            key={key || index}
                            className={
                              status === 'duplicate'
                                ? 'bg-amber-50/40'
                                : ''
                            }
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={eligible && selectedSet.has(key)}
                                disabled={!eligible || busy}
                                onChange={() => toggleOne(key)}
                              />
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                              {row.date || '—'}
                            </td>

                            <td className="max-w-[260px] px-4 py-3">
                              <p className="truncate font-medium text-slate-900">
                                {row.description || 'QuickBooks posting'}
                              </p>

                              <p className="mt-1 text-xs text-slate-400">
                                {row.transactionType || row.type || ''}
                                {row.transactionId
                                  ? ` · ID: ${row.transactionId}`
                                  : ''}
                              </p>
                            </td>

                            <td className="px-4 py-3">
                              {row.accountName || 'Unknown'}
                            </td>

                            <td className="px-4 py-3">
                              {row.profitYoAccountName
                                ? `${row.profitYoAccountCode
                                    ? `${row.profitYoAccountCode} - `
                                    : ''}${row.profitYoAccountName}`
                                : 'Not mapped'}
                            </td>

                            <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                              {formatMoney(
                                row.amount,
                                row.currency || organizationCurrency
                              )}
                            </td>

                            <td className="px-4 py-3">
                              {status === 'duplicate' ? (
                                <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
                                  Duplicate
                                </span>
                              ) : status === 'ready' ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                                  Mapped · Review
                                </span>
                              ) : (
                                <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                                  Needs mapping
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {filteredRows.length === 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-12 text-center text-slate-500"
                          >
                            No postings match your filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">
                  {selectedSet.size} posting(s) selected
                </p>

                <p className="text-xs text-slate-500">
                  Maximum {MAX_SELECTED} per batch
                </p>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-800">
                General Ledger report amounts are not verified
                debit/credit journal entries. These postings
                must not be approved through the CSV
                approve-import function.
              </div>

            </div>
          )}

          {/* ===============================================
             STEP 4 — FINISH
          =============================================== */}

          {step === 4 && (
            <div className="flex min-h-[350px] flex-col items-center justify-center text-center">

              <FiCheckCircle className="text-6xl text-emerald-600" />

              <h3 className="mt-5 text-xl font-semibold text-slate-900">
                Staging completed
              </h3>

              <p className="mt-2 max-w-lg text-sm text-slate-500">
                QuickBooks postings have been staged for
                accounting review. No live ProfitYo
                transactions were posted.
              </p>

              <div className="mt-6 rounded-2xl bg-slate-50 px-6 py-5">
                <p className="text-xs text-slate-500">
                  Import batch
                </p>

                <p className="mt-1 break-all text-sm font-semibold">
                  {result?.batchId || result?.importBatchId || '—'}
                </p>

                <p className="mt-4 text-2xl font-semibold text-emerald-700">
                  {result?.stagedCount ?? 0}
                </p>

                <p className="text-xs text-slate-500">
                  Newly staged postings
                </p>
              </div>

              <p className="mt-6 max-w-lg text-xs leading-5 text-amber-700">
                Final ledger approval remains disabled until
                complete, balanced QuickBooks journal entries
                and atomic posting are implemented.
              </p>

            </div>
          )}

        </div>

        {/* FOOTER */}

        <footer className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 md:px-7">

          <button
            type="button"
            onClick={() => {
              if (step === 0 || step === 4) {
                close();
              } else {
                setStep((current) => current - 1);
                setErrorMessage('');
                setSuccessMessage('');
              }
            }}
            disabled={busy || savingMappings}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {step > 0 && step < 4 && <FiArrowLeft />}

            {step === 0
              ? 'Cancel'
              : step === 4
                ? 'Close'
                : 'Back'}
          </button>

          {step === 0 && (
            <button
              type="button"
              onClick={preview}
              disabled={busy || !rangeValid}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? <FiLoader className="animate-spin" /> : <FiSearch />}

              {busy ? 'Loading...' : 'Preview Transactions'}

              {!busy && <FiArrowRight />}
            </button>
          )}

          {step === 1 && (
            <button
              type="button"
              onClick={() => setStep(2)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white"
            >
              Match Accounts
              <FiArrowRight />
            </button>
          )}

          {step === 2 && (
            <button
              type="button"
              onClick={() => {
                setErrorMessage('');
                setStep(3);
              }}
              disabled={savingMappings}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Review Transactions
              <FiArrowRight />
            </button>
          )}

          {step === 3 && (
            <button
              type="button"
              onClick={stage}
              disabled={busy || selectedSet.size === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiCheckCircle />
              )}

              {busy
                ? 'Staging...'
                : `Stage ${selectedSet.size} posting(s)`}
            </button>
          )}

          {step === 4 && (
            <button
              type="button"
              onClick={close}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white"
            >
              Finish
            </button>
          )}

        </footer>

      </section>
    </div>
  );
}

export { LegacyQuickBooksImportModal };

const sourceSteps = [
  'Select Account',
  'Transactions',
  'Review & Match',
  'Finish'
];

const sourceCategoryName = (row) => str(
  row.categoryName ??
  row.categoryAccountName ??
  row.category?.name ??
  row.categoryAccount?.name
);

const sourceCategoryId = (row) => str(
  row.categoryAccountId ??
  row.quickbooksCategoryId ??
  row.category?.id ??
  row.categoryAccount?.id
);

const sourceRowKey = (row, index) => str(
  row.id ??
  row.transactionId ??
  row.quickbooksTransactionId
) || `source-row-${index}`;

const sourceDuplicate = (row) =>
  row.isDuplicate === true ||
  String(row.status || '').toLowerCase() === 'duplicate';

const sourceStatus = (row) => {
  if (sourceDuplicate(row)) return 'Duplicate';
  if (!sourceCategoryName(row)) return 'Missing Category';
  if (row.categoryVerified === false) return 'Unverified Category';
  return 'Ready for review';
};

function SourceStepBar({ step }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {sourceSteps.map((label, index) => (
        <React.Fragment key={label}>
          <div className={`flex shrink-0 items-center gap-2 text-xs font-semibold ${
            index <= step ? 'text-slate-900' : 'text-slate-400'
          }`}>
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${
              index < step
                ? 'bg-emerald-600 text-white'
                : index === step
                  ? 'bg-slate-950 text-white'
                  : 'bg-slate-100 text-slate-400'
            }`}>
              {index < step ? <FiCheckCircle /> : index + 1}
            </span>
            <span className="hidden md:inline">{label}</span>
          </div>
          {index < sourceSteps.length - 1 && (
            <div className="h-px min-w-3 flex-1 bg-slate-200" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function QuickBooksImportModal({
  isOpen,
  onClose,
  organizationId,
  organizationName = '',
  organizationCurrency = 'USD',
  externalError = null,
  error: legacyError = null
}) {
  const [step, setStep] = useState(0);
  const [dates, setDates] = useState(initialDates);
  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const requestRef = useRef(0);
  const busyRef = useRef(false);

  const selectedSource = useMemo(
    () => sourceAccounts.find((account) => account.id === sourceAccountId) || null,
    [sourceAccounts, sourceAccountId]
  );

  const rangeValid = useMemo(() => {
    if (!validDate(dates.startDate) || !validDate(dates.endDate)) return false;
    const days = (Date.parse(dates.endDate) - Date.parse(dates.startDate)) / 86400000;
    return days >= 0 && days <= 366;
  }, [dates]);

  const rowEntries = useMemo(
    () => rows.map((row, index) => ({
      row,
      key: sourceRowKey(row, index)
    })),
    [rows]
  );

  const selectedRows = useMemo(
    () => rowEntries.filter(({ row, key }) =>
      selectedKeys.includes(key) && !sourceDuplicate(row)
    ),
    [rowEntries, selectedKeys]
  );

  const counts = useMemo(() => ({
    total: rows.length,
    duplicates: rows.filter(sourceDuplicate).length,
    unverified: rows.filter((row) =>
      !sourceDuplicate(row) && sourceCategoryName(row) && row.categoryVerified === false
    ).length,
    missing: rows.filter((row) =>
      !sourceDuplicate(row) && !sourceCategoryName(row)
    ).length
  }), [rows]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rowEntries.filter(({ row }) => {
      const status = sourceStatus(row);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!needle) return true;
      return [
        row.description,
        row.payee,
        sourceCategoryName(row),
        row.transactionType,
        row.type
      ].map(str).join(' ').toLowerCase().includes(needle);
    });
  }, [rowEntries, search, statusFilter]);

  const mappingGroups = useMemo(() => {
    const groups = new Map();
    const add = (id, name, mappedId, mappedName) => {
      const normalizedId = str(id);
      const normalizedName = str(name);
      if (!normalizedId || !normalizedName || mappedId) return;
      if (!groups.has(normalizedId)) {
        groups.set(normalizedId, {
          id: normalizedId,
          name: normalizedName,
          mappedName: str(mappedName)
        });
      }
    };

    if (selectedSource) {
      add(
        selectedSource.id,
        selectedSource.fullyQualifiedName || selectedSource.name,
        selectedSource.profitYoAccountId,
        selectedSource.profitYoAccountName
      );
    }

    selectedRows.forEach(({ row }) => {
      add(
        sourceCategoryId(row),
        sourceCategoryName(row),
        row.profitYoCategoryAccountId || row.profitYoAccountId,
        row.profitYoCategoryAccountName || row.profitYoAccountName
      );
    });

    return [...groups.values()];
  }, [selectedRows, selectedSource]);

  const selectedMappings = useMemo(() => {
    const used = new Set();
    return mappingGroups.flatMap((group) => {
      const profitYoAccountId = assignments[group.id];
      if (!profitYoAccountId || used.has(profitYoAccountId)) return [];
      used.add(profitYoAccountId);
      return [{
        quickbooksAccountId: group.id,
        profitYoAccountId
      }];
    });
  }, [assignments, mappingGroups]);

  const resetTransactions = () => {
    setRows([]);
    setSelectedKeys([]);
    setAssignments({});
    setSearch('');
    setStatusFilter('all');
    setSuccessMessage('');
  };

  useEffect(() => {
    if (!isOpen || !organizationId) return undefined;
    let active = true;
    const loadAccounts = async () => {
      setAccountsLoading(true);
      setErrorMessage('');
      try {
        const [sourceData, profitYoAccounts] = await Promise.all([
          getQuickBooksSourceAccounts({ organizationId }),
          getProfitYoAccountsForQuickBooks(organizationId)
        ]);
        if (!active) return;
        setSourceAccounts(sourceData.accounts);
        setCompanyName(sourceData.companyName || '');
        setConnectionId(sourceData.connectionId || '');
        setAccounts(profitYoAccounts);
      } catch (error) {
        if (active) {
          setErrorMessage(error?.message || 'Unable to load QuickBooks source accounts.');
        }
      } finally {
        if (active) setAccountsLoading(false);
      }
    };
    loadAccounts();
    return () => {
      active = false;
      requestRef.current += 1;
    };
  }, [isOpen, organizationId]);

  const close = () => {
    if (busyRef.current || savingMappings) return;
    requestRef.current += 1;
    onClose?.();
  };

  const loadTransactions = async () => {
    if (busyRef.current || !organizationId || !selectedSource || !rangeValid) return;
    busyRef.current = true;
    const token = ++requestRef.current;
    setBusy(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const data = await getQuickBooksSourceTransactions({
        organizationId,
        connectionId,
        sourceAccountId: selectedSource.id,
        startDate: dates.startDate,
        endDate: dates.endDate
      });
      if (token !== requestRef.current) return;
      setRows(data.transactions);
      setSelectedKeys(data.transactions
        .map((row, index) => ({ row, key: sourceRowKey(row, index) }))
        .filter(({ row }) => !sourceDuplicate(row))
        .map(({ key }) => key));
      setStep(1);
    } catch (error) {
      if (token === requestRef.current) {
        setErrorMessage(error?.message || 'Unable to load QuickBooks transactions.');
      }
    } finally {
      if (token === requestRef.current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const toggleTransaction = (key) => {
    setSelectedKeys((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  };

  const saveMappings = async () => {
    if (savingMappings || selectedMappings.length === 0) return;
    const token = ++requestRef.current;
    setSavingMappings(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await saveQuickBooksMappings({
        organizationId,
        connectionId,
        mappings: selectedMappings
      });
      const [sourceData, transactionData] = await Promise.all([
        getQuickBooksSourceAccounts({ organizationId }),
        getQuickBooksSourceTransactions({
          organizationId,
          connectionId,
          sourceAccountId,
          startDate: dates.startDate,
          endDate: dates.endDate
        })
      ]);
      if (token !== requestRef.current) return;
      setSourceAccounts(sourceData.accounts);
      setCompanyName(sourceData.companyName || companyName);
      setConnectionId(sourceData.connectionId || connectionId);
      setRows(transactionData.transactions);
      setAssignments({});
      setSuccessMessage(
        'Mappings saved and refreshed. Mapping an account does not verify a transaction’s journal entries or split categories.'
      );
    } catch (error) {
      if (token === requestRef.current) {
        setErrorMessage(error?.message || 'Unable to save account mappings.');
      }
    } finally {
      if (token === requestRef.current) setSavingMappings(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-sm">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="qb-import-title"
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between border-b border-slate-100 px-5 py-5 md:px-7">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
              ProfitYo · QuickBooks Online
            </p>
            <h2 id="qb-import-title" className="mt-1 text-xl font-semibold text-slate-950">
              Import transactions
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {organizationName || 'Active organization'}
              {companyName ? ` · ${companyName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={accountsLoading || busy || savingMappings}
            className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            aria-label="Close QuickBooks import"
          >
            <FiX size={20} />
          </button>
        </header>

        <div className="border-b border-slate-100 px-5 py-4 md:px-7">
          <SourceStepBar step={step} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-7">
          {(errorMessage || externalError || legacyError) && (
            <div role="alert" className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <p>{errorMessage || externalError || legacyError}</p>
            </div>
          )}
          {successMessage && (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
              <FiCheckCircle className="mt-0.5 shrink-0" />
              <p>{successMessage}</p>
            </div>
          )}

          {step === 0 && (
            <div className="mx-auto max-w-2xl space-y-6 py-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Select a QuickBooks account</h3>
                <p className="mt-2 text-sm text-slate-500">
                  Choose a Bank or Credit Card source account, then select the transaction date range.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Connected QuickBooks company
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {accountsLoading ? 'Loading company…' : companyName || 'Company name unavailable'}
                </p>
              </div>
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-800">Source Account</span>
                <SearchableSelect
                  options={sourceAccounts}
                  value={sourceAccountId}
                  onChange={(value) => {
                    setSourceAccountId(value);
                    resetTransactions();
                    setErrorMessage('');
                  }}
                  disabled={accountsLoading || sourceAccounts.length === 0}
                  placeholder={accountsLoading ? 'Loading source accounts…' : 'Select a Bank or Credit Card account'}
                  searchPlaceholder="Search source accounts..."
                  emptyMessage="No QuickBooks source accounts found."
                  getLabel={(account) => account.fullyQualifiedName || account.name}
                  getSearchText={(account) => [
                    account.name,
                    account.fullyQualifiedName,
                    account.accountType,
                    account.accountSubType
                  ]}
                  renderOption={(account) => (
                    <span className="flex flex-wrap items-center gap-2">
                      <span>{account.fullyQualifiedName || account.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                        {account.accountType}
                      </span>
                    </span>
                  )}
                />
                {!accountsLoading && sourceAccounts.length === 0 && (
                  <span className="block text-xs text-slate-500">
                    No Bank or Credit Card accounts were returned by QuickBooks.
                  </span>
                )}
              </label>

              {selectedSource && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold text-slate-500">Account Type</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {selectedSource.accountType}
                      {selectedSource.accountSubType ? ` · ${selectedSource.accountSubType}` : ''}
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs font-semibold text-slate-500">ProfitYo linked account</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {selectedSource.profitYoAccountName || 'Not mapped'}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-semibold text-slate-800">Start Date</span>
                  <input
                    type="date"
                    value={dates.startDate}
                    onChange={(event) => {
                      setDates((current) => ({ ...current, startDate: event.target.value }));
                      resetTransactions();
                    }}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-semibold text-slate-800">End Date</span>
                  <input
                    type="date"
                    value={dates.endDate}
                    onChange={(event) => {
                      setDates((current) => ({ ...current, endDate: event.target.value }));
                      resetTransactions();
                    }}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3"
                  />
                </label>
              </div>
              {dates.startDate && dates.endDate && !rangeValid && (
                <p className="text-xs text-rose-600">
                  Enter a valid date range of 366 days or fewer.
                </p>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <SummaryCard label="Transactions" value={counts.total} />
                <SummaryCard label="Duplicates" value={counts.duplicates} />
                <SummaryCard label="Unverified categories" value={counts.unverified} color="amber" />
                <SummaryCard label="Missing categories" value={counts.missing} color="amber" />
              </div>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                Categories shown are those returned by QuickBooks. Unverified or missing categories are not approved, and duplicates cannot be selected.
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="relative min-w-[220px] flex-1">
                  <FiSearch className="absolute left-3 top-3.5 text-slate-400" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search description, category, or type..."
                    className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-sm"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="all">All category statuses</option>
                  <option value="Ready for review">Ready for review</option>
                  <option value="Unverified Category">Unverified Category</option>
                  <option value="Missing Category">Missing Category</option>
                  <option value="Duplicate">Duplicate</option>
                </select>
              </div>
              {busy ? (
                <div className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200 py-16 text-sm text-slate-600">
                  <FiLoader className="animate-spin" />
                  Loading QuickBooks transactions…
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="max-h-[460px] overflow-auto">
                    <table className="w-full min-w-[1050px] text-left text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr>
                          <th className="px-4 py-3">Select</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Description</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3">QuickBooks Category</th>
                          <th className="px-4 py-3">ProfitYo Category</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRows.map(({ row, key }) => {
                          const status = sourceStatus(row);
                          return (
                            <tr key={key} className={status === 'Duplicate' ? 'bg-amber-50/40' : ''}>
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedKeys.includes(key)}
                                  disabled={sourceDuplicate(row)}
                                  onChange={() => toggleTransaction(key)}
                                  aria-label={`Select transaction ${key}`}
                                />
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                                {row.date || row.transactionDate || '—'}
                              </td>
                              <td className="max-w-[240px] px-4 py-3">
                                <p className="truncate font-medium text-slate-900">
                                  {row.payee || row.description || '—'}
                                </p>
                              </td>
                              <td className="px-4 py-3">{row.transactionType || row.type || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                                {formatMoney(row.amount, row.currency || organizationCurrency)}
                              </td>
                              <td className="px-4 py-3">{sourceCategoryName(row) || '—'}</td>
                              <td className="px-4 py-3">
                                {row.profitYoCategoryAccountName || row.profitYoAccountName || 'Not mapped'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                                  status === 'Ready for review'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : status === 'Duplicate'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-rose-100 text-rose-700'
                                }`}>
                                  {status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {!filteredRows.length && (
                          <tr>
                            <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                              {rows.length ? 'No transactions match your search or filter.' : 'No transactions were returned for this account and date range.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <p className="text-xs text-slate-500">
                {selectedRows.length} non-duplicate transaction(s) selected.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <SummaryCard label="Selected transactions" value={selectedRows.length} />
                <SummaryCard label="Duplicates excluded" value={counts.duplicates} />
                <SummaryCard label="Unmapped accounts" value={mappingGroups.length} color="amber" />
                <SummaryCard label="Eligible for staging" value={0} />
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                Saving an account mapping links account references only. It does not verify the transaction’s journal entries or split categories.
              </div>
              {mappingGroups.length > 0 ? (
                <div className="space-y-3">
                  {mappingGroups.map((group) => {
                    const used = new Set(Object.values(assignments).filter(Boolean));
                    const options = accounts.filter((account) =>
                      account.id === assignments[group.id] || !used.has(account.id)
                    );
                    return (
                      <div key={group.id} className="grid gap-4 rounded-2xl border border-slate-200 p-4 lg:grid-cols-2">
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            QuickBooks account/category
                          </p>
                          <p className="mt-1 text-sm font-semibold text-slate-900">{group.name}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {group.mappedName || 'No existing ProfitYo mapping'}
                          </p>
                        </div>
                        <div>
                          <p className="mb-2 text-xs font-semibold text-slate-700">
                            Map to existing ProfitYo account
                          </p>
                          <SearchableSelect
                            options={options}
                            value={assignments[group.id] || ''}
                            onChange={(value) => {
                              setAssignments((current) => ({
                                ...current,
                                [group.id]: value || ''
                              }));
                              setErrorMessage('');
                            }}
                            disabled={savingMappings || accounts.length === 0}
                            placeholder={accounts.length ? 'Select existing account…' : 'No active accounts available'}
                            searchPlaceholder="Search Chart of Accounts…"
                            emptyMessage="No active ProfitYo accounts found."
                            getLabel={(account) => `${account.code || 'No code'} — ${account.name}`}
                            getSearchText={(account) => [
                              account.code,
                              account.name,
                              account.type,
                              account.sub_type
                            ]}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={saveMappings}
                    disabled={savingMappings || selectedMappings.length === 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {savingMappings ? <FiLoader className="animate-spin" /> : <FiLink2 />}
                    Save {selectedMappings.length} mapping(s)
                  </button>
                </div>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800">
                  No unmapped QuickBooks accounts or categories were found among the selected transactions.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryCard
                  label="Unverified categories"
                  value={selectedRows.filter(({ row }) => row.categoryVerified === false && sourceCategoryName(row)).length}
                  color="amber"
                />
                <SummaryCard
                  label="Missing categories"
                  value={selectedRows.filter(({ row }) => !sourceCategoryName(row)).length}
                  color="amber"
                />
                <SummaryCard label="Eligible for staging" value={0} />
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <div className="max-h-[280px] overflow-auto">
                  <table className="w-full min-w-[700px] text-left text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3">QuickBooks Category</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedRows.map(({ row, key }) => (
                        <tr key={key}>
                          <td className="whitespace-nowrap px-4 py-3">{row.date || row.transactionDate || '—'}</td>
                          <td className="px-4 py-3">{row.payee || row.description || '—'}</td>
                          <td className="px-4 py-3">{sourceCategoryName(row) || 'Missing Category'}</td>
                          <td className="px-4 py-3">{sourceStatus(row)}</td>
                        </tr>
                      ))}
                      {!selectedRows.length && (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-slate-500">
                            No transactions selected.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex min-h-[350px] flex-col items-center justify-center text-center">
              <FiAlertCircle className="text-6xl text-amber-500" />
              <h3 className="mt-5 text-xl font-semibold text-slate-900">
                Review Complete — Awaiting Import Support
              </h3>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
                Your selection and account mappings have been reviewed. The deployed backend does not yet provide a verified source-transaction staging contract, so nothing has been staged or posted to the ProfitYo ledger.
              </p>
              <div className="mt-6 grid w-full max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryCard label="Selected transactions" value={selectedRows.length} />
                <SummaryCard label="Duplicates excluded" value={counts.duplicates} />
                <SummaryCard
                  label="Unverified / missing"
                  value={selectedRows.filter(({ row }) => row.categoryVerified === false || !sourceCategoryName(row)).length}
                  color="amber"
                />
              </div>
              <p className="mt-5 max-w-xl text-xs leading-5 text-slate-500">
                The legacy stage action expects General Ledger preview keys. Source transaction identifiers are not assumed to be compatible, and the final Stage / Import action remains disabled for safety.
              </p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 md:px-7">
          <button
            type="button"
            onClick={() => {
              if (step === 0 || step === 3) {
                close();
              } else {
                setStep((current) => current - 1);
                setErrorMessage('');
                setSuccessMessage('');
              }
            }}
            disabled={accountsLoading || busy || savingMappings}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {step > 0 && step < 3 && <FiArrowLeft />}
            {step === 0 ? 'Cancel' : step === 3 ? 'Close' : 'Back'}
          </button>

          {step === 0 && (
            <button
              type="button"
              onClick={loadTransactions}
              disabled={!selectedSource || !rangeValid || accountsLoading || busy}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? <FiLoader className="animate-spin" /> : <FiSearch />}
              {busy ? 'Loading…' : 'Continue'}
              {!busy && <FiArrowRight />}
            </button>
          )}

          {step === 1 && (
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={selectedRows.length === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Review & Match
              <FiArrowRight />
            </button>
          )}

          {step === 2 && (
            <button
              type="button"
              onClick={() => {
                setErrorMessage('');
                setSuccessMessage('');
                setStep(3);
              }}
              disabled={savingMappings}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Finish Review
              <FiArrowRight />
            </button>
          )}

          {step === 3 && (
            <button
              type="button"
              disabled
              title="Source-transaction staging is not yet supported by the deployed backend."
              className="rounded-xl bg-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-600"
            >
              Stage / Import unavailable
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}